import type { InterfaceToken } from '@morpho-org/dapp-core'

import { MathLib } from '@morpho-org/blue-sdk'
import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { isDefined } from '@morpho-org/morpho-ts'
import { formatUnits } from 'viem'

/**
 * Calculates the projected monthly earnings in USD based on the annual percentage yield (APY), position and price.
 * The math formula used is: (1 + yearlyNetApy) ^(1/12) - 1
 *
 * @param {bigint | undefined | null} netApy - The annual percentage yield of the vault.
 * @param {bigint | undefined} position - The current position assets in the vault.
 * @param {InterfaceToken} vaultAsset - The asset used in the vault for conversion to USD.
 * @returns {bigint} The formatted projected monthly earnings in USD.
 */
export const calculateProjectedEarningsMonthly = (
  netApy: bigint | undefined | null,
  position: bigint | undefined,
  vaultAsset: InterfaceToken | null | undefined
) => {
  if (!isDefined(netApy) || !isDefined(position) || !isDefined(vaultAsset)) return undefined

  const finalPositionUsd = vaultAsset.toUsd(position)
  if (finalPositionUsd === undefined) return undefined

  const monthlyApy = Math.pow(1 + Number(formatUnits(netApy, 18)), 1 / 12) - 1

  return MathLib.wMulDown(finalPositionUsd, safeParseNumber(monthlyApy, 18))
}

/**
 * Calculates the projected yearly earnings in USD based on the annual percentage yield (APY), position and price.
 *
 * @param {bigint | undefined | null} netApy - The annual percentage yield of the vault.
 * @param {bigint | undefined} position - The current position assets in the vault.
 * @param {InterfaceToken} vaultAsset - The asset used in the vault for conversion to USD.
 * @returns {bigint} The formatted projected yearly earnings in USD.
 */
export const calculateProjectedEarningsYearly = (
  netApy: bigint | undefined | null,
  position: bigint | undefined,
  vaultAsset: InterfaceToken | null | undefined
) => {
  if (!isDefined(netApy) || !isDefined(position) || !isDefined(vaultAsset)) return undefined

  const assetNumberYearly = MathLib.wMulDown(netApy, position)
  const projectedEarningsAssetUsdYearly = vaultAsset.toUsd(assetNumberYearly)

  return projectedEarningsAssetUsdYearly
}
